// @ts-nocheck
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getBillTosDataView,
    getCurrentBillToState,
} from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Select, { SelectPresentationProps } from "@store/MobiusOverrides/Select/Select";
import constants from "@store/constants";
import { useUserNotificationPreferencesContext } from "@store/Framework/Components/UserNotificationPreferencesContext/UserNotificationPreferencesContext";
import setFieldValue from "@store/Framework/Store/Greif/Components/InviteYourColleague/Handlers/SetFieldValue";
import { InviteYourColleagueFormContext } from "@store/Library/Widgets/InviteYourColleague/InviteYourColleagueForm";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import React, { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    isRequired = "isRequired",
    fieldLabel = "fieldLabel",
    soldTosToDisplay = "soldTosToDisplay",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.isRequired]: boolean;
        [fields.fieldLabel]: string;
        [fields.soldTosToDisplay]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    soldTo: state.greif.components.inviteYourColleagueForm.fieldValues["soldTo"],
    billToDataview: getBillTosDataView(state, {
        page: 1,
        pageSize: Number(props.fields.soldTosToDisplay),
    }),
    currentBillToState: getCurrentBillToState(state),
});

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentBillTo,
    loadBillTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SoldToStyles {
    soldToTextField?: TextFieldPresentationProps;
    soldToSelect?: SelectPresentationProps;
}

export const soldToStyles: SoldToStyles = {};

const styles = soldToStyles;

const SoldTo: FC<Props> = ({
    fields,
    soldTo,
    currentBillToState,
    billToDataview,
    setFieldValue,
    loadBillTos,
    loadCurrentBillTo,
}) => {
    const { isRequired, fieldLabel, soldTosToDisplay } = fields;
    const inviteYourColleagueFormContext = useContext(InviteYourColleagueFormContext);
    const [soldToErrorMessage, setSoldToErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [soldToData, setSoldToData] = useState<any[]>();
    const { customerSoldTo } = useUserNotificationPreferencesContext();

    const { h } = useQueryString();

    const currentBillTo = currentBillToState && currentBillToState.value;
    const allBillToDataview = billToDataview && billToDataview.value;

    const params: GetBillTosApiParameter = {
        page: 1,
        pageSize: Number(fields.soldTosToDisplay),
    };

    useEffect(() => {
        if (h && customerSoldTo) {
            setSoldToData(customerSoldTo.soldTos);
        }

        if (!billToDataview.isLoading && !billToDataview.value && !h) {
            loadCurrentBillTo();
            loadBillTos(params);
        }

        if (allBillToDataview && currentBillTo && !h) {
            setSoldToData(billToDataview.value);
            setFieldValue({ key: "soldTo", value: currentBillToState.value.customerNumber });
        }
    }, [customerSoldTo, currentBillToState.value, billToDataview.value]);

    useEffect(() => {
        if (typeof soldTo === "undefined") {
            if (currentBillTo) {
                setFieldValue({ key: "soldTo", value: currentBillToState.value.customerNumber });
            }
            if (h) {
                setFieldValue({ key: "soldTo", value: "default" });
            }
        }
    }, [soldTo]);

    useEffect(() => {
        inviteYourColleagueFormContext.validators.soldTo = validateSoldTo;
        if (typeof soldTo !== "undefined") {
            validateSoldTo();
        }
    }, [soldTo]);

    const validateSoldTo = () => {
        const errorMessage = !soldTo ? (translate(`${fieldLabel} is required`) as string) : "";
        setSoldToErrorMessage(errorMessage);
        return !errorMessage;
    };

    const soldToChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "shipTo", value: "default" });
        setFieldValue({ key: "soldTo", value: event.target.value });
    };

    return (
        <>
            <Select
                label={fieldLabel}
                value={String(soldTo) || ""}
                onChange={soldToChangeHandler}
                required={isRequired}
                error={soldToErrorMessage}
                disabled={!soldToData}
                {...styles.soldToSelect}
            >
                <option value="default" disabled>
                    {translate("Select a Sold-To")}
                </option>
                {soldToData?.map((soldTo, id) => (
                    <option value={soldTo.customerNumber} key={id}>
                        {soldTo.label}
                    </option>
                ))}
            </Select>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SoldTo),
    definition: {
        displayName: "Sold-To",
        group: constants.widgetGroup.inviteYourColleague,
        icon: "input-pipe",
        allowedContexts: [constants.pageType.inviteYourColleague, constants.pageType.notificationPreferences],
        fieldDefinitions: [
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.fieldLabel,
                displayName: "Field Label",
                editorTemplate: "TextField",
                defaultValue: "Customer Sold-To",
                fieldType: "Translatable",
            },
            {
                name: fields.soldTosToDisplay,
                displayName: "SoldTos to display",
                editorTemplate: "IntegerField",
                max: 250,
                defaultValue: 250,
                tooltip: "Platform default: 20",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
